import bus from '@/lib/event_bus';
import {
  isApplicationExpired,
  REJECT_ROUTE_NAMES,
  redirectToConnectOffers,
  shouldSkipRejectScreen,
} from '@/lib/freo_connect_redirect';
import {
  EXPIRED_REAPPLY_MESSAGE,
  restartExpiredApplication,
} from '@/lib/expired_reapply';

/**
 * Sends a rejected customer who has Freo Connect partner offers straight to
 * Connect, without the reject screen in between. RejectScreen.vue used to do
 * this itself, but only after mounting and fetching /connect/options, so
 * /qual/rejected sat on screen for a second or two on the way through.
 *
 * Registered from main.ts rather than router/index.ts on purpose: the offers
 * lookup reaches @/api, which reaches the router again, and router/index.ts
 * importing it would close that loop. main.ts is an entry — nothing imports it
 * — so hanging the guard there keeps the dependency graph acyclic.
 *
 * Registering after the auth guard in router/index.ts also means this only ever
 * runs on a navigation that guard has already allowed.
 */
// Long enough that a healthy redirect always wins the race, short enough that a
// failed one is not left sitting on screen.
const REDIRECT_FAILSAFE_MS = 6000;

export function registerSkipRejectScreenGuard(router: any): void {
  router.beforeEach(async (to: any, from: any, next: any) => {
    if (!REJECT_ROUTE_NAMES.includes(to.name)) {
      next();
      return;
    }
    // An expired application is restarted rather than shown. The reject screen
    // has nothing to offer these customers but the re-apply button, so the
    // re-application is made for them and they are taken to the quick profile
    // form, with a notification saying why they are being asked again.
    //
    // Wrapped, like the Connect check below it, because a guard that throws
    // never calls next() — the navigation would hang on a blank page. Anything
    // unexpected here has to end with the customer on the reject screen.
    try {
      if (isApplicationExpired()) {
        const qualHomeRoute = await restartExpiredApplication();
        if (qualHomeRoute) {
          next({ name: qualHomeRoute });
          // Emitted after next() so it appears alongside the form rather than
          // the screen being left. <notifications> lives in App.vue and outlives
          // every navigation, so nothing here is lost in the handover.
          bus.$emit('notify', {
            type: 'info',
            message: EXPIRED_REAPPLY_MESSAGE,
            showClose: true,
          });
          return;
        }
        // Could not re-apply — carry on to the reject screen, which is where
        // this customer would have gone anyway. shouldSkipRejectScreen() holds
        // expired applications back from Connect, so they land on the screen
        // with the re-apply button rather than being forwarded to a partner.
        next();
        return;
      }
    } catch (error) {
      console.log('Expired application re-apply failed', error);
      bus.$emit('loader');
      next();
      return;
    }
    try {
      if (await shouldSkipRejectScreen()) {
        bus.$emit('loader', {
          show: true,
          message: 'Finding eligible partners...',
        });
        redirectToConnectOffers();
        // Deliberately no next() call here. The browser is already leaving for
        // Connect, and letting the reject screen mount is the flash this guard
        // exists to remove. next(false) would abort instead, but in vue-router
        // 3.6 an aborted navigation rejects the promise router.push() returns,
        // and none of the reject-screen callers catch it — that would turn a
        // clean redirect into an unhandled rejection in Sentry. Leaving the
        // navigation pending stops it just as dead, without the noise.
        //
        // Failsafe: if the document is somehow still here, resume the
        // navigation so the customer lands on the reject screen rather than
        // sitting on a frozen page. A completed redirect tears this timer down
        // with the document, so it only ever fires when the redirect did not.
        setTimeout(() => {
          bus.$emit('loader');
          next();
        }, REDIRECT_FAILSAFE_MS);
        return;
      }
    } catch (error) {
      // Never block the reject screen on this check — a failure here just means
      // the customer sees the screen, which is the pre-existing behaviour.
      console.log('Freo Connect pre-check failed', error);
    }
    next();
  });
}
